import classNames from 'classnames';
import React from 'react';
import { I18n } from 'common/i18n';
import DebouncedInput from '../../shared/DebouncedInput';
import {
  MEASURE_AXIS_SCALE_CUSTOM,
  MEASURE_AXIS_SCALE_MIN_TO_MAX,
  MEASURE_AXIS_SCALE_ZERO_TO_MAX,
} from 'common/authoring_workflow/constants';

export interface AxisScaleSelectorProps {
  axisScale: string;
  disabled: boolean;
  disableScaleMinToMaxValue: boolean;
  maxLimit: number | string;
  minLimit: number | string;
  onMaxValueTextboxChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onMinValueTextboxChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRadioButtonChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const renderRadioButton = ({
  checked,
  className,
  disabled,
  id,
  label,
  onChange,
  value,
}: {
  checked: boolean;
  className: string;
  disabled: boolean;
  id: string;
  label: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  value: string;
}) => {
  return (
    <div className={className}>
      <input
        checked={checked}
        disabled={disabled}
        id={id}
        name="measure-axis-scale"
        onChange={onChange}
        type="radio"
        value={value}
      />
      <label htmlFor={id}>
        <span className="fake-radiobutton" />
        <div className="translation-within-label">{label}</div>
      </label>
    </div>
  );
};

const renderTextbox = ({
  id,
  label,
  onChange,
  value,
}: {
  id: string;
  label: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  value: number | string;
}) => {
  return (
    <div>
      <label className="block-label" htmlFor={id}>
        {label}
      </label>
      <DebouncedInput onChange={onChange} type="number" value={value} />
    </div>
  );
};

export const AxisScaleSelector = ({
  axisScale,
  disabled,
  disableScaleMinToMaxValue,
  maxLimit,
  minLimit,
  onMaxValueTextboxChange,
  onMinValueTextboxChange,
  onRadioButtonChange,
}: AxisScaleSelectorProps) => {
  const className = classNames('authoring-field radiobutton', {
    disabled,
  });

  const zeroToMaxScaleRadioButton = renderRadioButton({
    checked: axisScale === MEASURE_AXIS_SCALE_ZERO_TO_MAX,
    className: 'measure-axis-scale-zero-to-max-container',
    disabled,
    id: 'measure-axis-scale-zero-to-max',
    label: I18n.t('shared.visualizations.panes.axis.fields.scale.scale_to_max_value'),
    onChange: onRadioButtonChange,
    value: MEASURE_AXIS_SCALE_ZERO_TO_MAX,
  });

  const minToMaxScaleRadioButton = renderRadioButton({
    checked: axisScale === MEASURE_AXIS_SCALE_MIN_TO_MAX,
    className: classNames('measure-axis-scale-min-to-max-container', { disabled: disableScaleMinToMaxValue }),
    disabled: disabled || disableScaleMinToMaxValue,
    id: 'measure-axis-scale-min-to-max',
    label: I18n.t('shared.visualizations.panes.axis.fields.scale.scale_to_min_and_max_values'),
    onChange: onRadioButtonChange,
    value: MEASURE_AXIS_SCALE_MIN_TO_MAX,
  });

  const customScaleRadioButton = renderRadioButton({
    checked: axisScale === MEASURE_AXIS_SCALE_CUSTOM,
    className: 'measure-axis-scale-custom-container',
    disabled,
    id: 'measure-axis-scale-custom',
    label: I18n.t('shared.visualizations.panes.axis.fields.scale.custom'),
    onChange: onRadioButtonChange,
    value: MEASURE_AXIS_SCALE_CUSTOM,
  });

  const textboxes =
    axisScale === MEASURE_AXIS_SCALE_CUSTOM ? (
      <div className="double-column-input-group">
        {renderTextbox({
          id: 'measure-axis-scale-custom-min',
          label: I18n.t('shared.visualizations.panes.axis.fields.scale.minimum'),
          onChange: onMinValueTextboxChange,
          value: minLimit,
        })}
        {renderTextbox({
          id: 'measure-axis-scale-custom-max',
          label: I18n.t('shared.visualizations.panes.axis.fields.scale.maximum'),
          onChange: onMaxValueTextboxChange,
          value: maxLimit,
        })}
      </div>
    ) : null;

  return (
    <div>
      <div className={className}>
        {zeroToMaxScaleRadioButton}
        {minToMaxScaleRadioButton}
        {customScaleRadioButton}
      </div>
      {textboxes}
    </div>
  );
};
